import React, { useMemo } from 'react';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { PaymentDetailRow, QueryResponse } from '../../../services/h311Api';

interface ResultTableProps {
  result: QueryResponse;
}

const fmtNum = (v: number | undefined | null): string => {
  const n = v ?? 0;
  return n.toLocaleString('zh-TW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const columns: ColumnsType<PaymentDetailRow & { _key: string }> = [
  {
    title: '',
    dataIndex: 'unapprovedMark',
    key: 'mark',
    width: 30,
    render: (v: string) => v === '*' ? <Tag color="red">*</Tag> : null,
  },
  {
    title: '請款日期',
    dataIndex: 'docDate',
    key: 'docDate',
    width: 100,
  },
  {
    title: '請款單號',
    dataIndex: 'docNo',
    key: 'docNo',
    width: 80,
  },
  {
    title: '部門',
    dataIndex: 'deptName',
    key: 'deptName',
    width: 100,
    ellipsis: true,
  },
  {
    title: '廠商/員工',
    dataIndex: 'vendorName',
    key: 'vendorName',
    width: 120,
    ellipsis: true,
  },
  {
    title: '類別',
    dataIndex: 'categoryName',
    key: 'categoryName',
    width: 130,
    ellipsis: true,
  },
  {
    title: '需要日期',
    dataIndex: 'needDate',
    key: 'needDate',
    width: 100,
  },
  {
    title: '支票到期日(1)',
    dataIndex: 'chkDate1',
    key: 'chkDate1',
    width: 110,
  },
  {
    title: '支票到期日(2)',
    dataIndex: 'chkDate2',
    key: 'chkDate2',
    width: 110,
  },
  {
    title: '發票號碼',
    dataIndex: 'invoiceNo',
    key: 'invoiceNo',
    width: 90,
  },
  {
    title: '發票日期',
    dataIndex: 'invoiceDate',
    key: 'invoiceDate',
    width: 100,
  },
  {
    title: '費用項目',
    dataIndex: 'expenseName',
    key: 'expenseName',
    width: 150,
    ellipsis: true,
  },
  {
    title: '歸屬公司',
    dataIndex: 'expenseCompany',
    key: 'expenseCompany',
    width: 100,
    ellipsis: true,
  },
  {
    title: '交運單號',
    dataIndex: 'shipNo',
    key: 'shipNo',
    width: 80,
  },
  {
    title: '幣別',
    dataIndex: 'currency',
    key: 'currency',
    width: 50,
    align: 'center',
  },
  {
    title: '未稅金額',
    dataIndex: 'amountExTax',
    key: 'amountExTax',
    width: 100,
    align: 'right',
    render: fmtNum,
  },
  {
    title: '含稅金額',
    dataIndex: 'amountInTax',
    key: 'amountInTax',
    width: 100,
    align: 'right',
    render: fmtNum,
  },
  {
    title: '票款別',
    dataIndex: 'payTypeName',
    key: 'payTypeName',
    width: 80,
  },
  {
    title: '入單人員',
    dataIndex: 'entryEmpName',
    key: 'entryEmpName',
    width: 80,
  },
];

const ResultTable: React.FC<ResultTableProps> = ({ result }) => {
  const dataSource = useMemo(
    () =>
      (result.details || []).map((row, i) => ({
        ...row,
        _key: `${row.docNo}-${row.batchNo}-${row.batchSeq}-${i}`,
      })),
    [result.details]
  );

  return (
    <div>
      <div className="mb-2 text-sm text-gray-600">
        核准狀態: <Tag>{result.approvalLabel}</Tag>
        共 <strong>{result.totalCount}</strong> 筆
        {result.approvalLabel === '全部' && (
          <span className="ml-2"><Tag color="red">*</Tag> 表請款單未核准</span>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="_key"
        size="small"
        scroll={{ x: 1800, y: 500 }}
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
      />
    </div>
  );
};

export default ResultTable;
